import { EMPTY_VALUE } from "../utils/formatters";

export function unwrapApiResponse(response) {
    return response?.data ?? response;
}

export function createDefaultSeriesFilters() {
    return {
        source_id: undefined,
        market_region: undefined,
        search_term: undefined,
        is_active: true,
    };
}

export function createDefaultQuoteFilters() {
    return {
        dateRange: null,
    };
}

export function buildSeriesQueryParams(filters, page, pageSize) {
    const params = {
        page,
        page_size: pageSize,
        is_active: filters.is_active,
    };

    if (filters.source_id) {
        params.source_id = filters.source_id;
    }
    if (filters.market_region) {
        params.market_region = filters.market_region;
    }
    if (filters.search_term) {
        params.search_term = filters.search_term;
    }

    return params;
}

export function buildDailyQuoteQueryParams(seriesId, filters, page, pageSize) {
    const params = {
        series_id: seriesId,
        page,
        page_size: pageSize,
    };

    if (filters?.dateRange && filters.dateRange.length === 2) {
        params.quote_date_from = filters.dateRange[0].format("YYYY-MM-DD");
        params.quote_date_to = filters.dateRange[1].format("YYYY-MM-DD");
    }

    return params;
}

export function formatMetricKeyLabel(metricKey) {
    if (!metricKey) {
        return EMPTY_VALUE;
    }

    return metricKey
        .split("_")
        .filter(Boolean)
        .map((segment) => segment.charAt(0) + segment.slice(1).toLowerCase())
        .join(" ");
}

export function normalizeMetricSchema(fieldSchema = [], quotes = []) {
    const schemaMap = new Map();

    const pushSchema = (item, index = 0) => {
        if (typeof item === "string") {
            const key = item.trim();
            if (!key) {
                return;
            }
            schemaMap.set(key, {
                key,
                label: formatMetricKeyLabel(key),
                unit: undefined,
                value_type: "number",
                precision: undefined,
                description: undefined,
                display_order: index,
            });
            return;
        }

        if (!item || typeof item !== "object") {
            return;
        }

        const key = String(item.key || item.metric_key || "").trim();
        if (!key) {
            return;
        }

        schemaMap.set(key, {
            key,
            label: item.label || item.name || formatMetricKeyLabel(key),
            unit: item.unit,
            value_type: item.value_type || "number",
            precision: Number.isFinite(item.precision) ? item.precision : undefined,
            description: item.description,
            display_order: Number.isFinite(item.display_order) ? item.display_order : index,
        });
    };

    fieldSchema.forEach((item, index) => pushSchema(item, index));

    let extraOrder = schemaMap.size + 100;
    quotes.forEach((quote) => {
        Object.keys(quote?.metrics || {}).forEach((key) => {
            if (schemaMap.has(key)) {
                return;
            }
            schemaMap.set(key, {
                key,
                label: formatMetricKeyLabel(key),
                unit: undefined,
                value_type: "number",
                precision: undefined,
                description: undefined,
                display_order: extraOrder,
            });
            extraOrder += 1;
        });
    });

    return Array.from(schemaMap.values()).sort((a, b) => {
        if (a.display_order !== b.display_order) {
            return a.display_order - b.display_order;
        }
        return a.key.localeCompare(b.key);
    });
}

export function formatMetricHeader(field) {
    if (!field) {
        return EMPTY_VALUE;
    }

    return field.unit ? `${field.label} (${field.unit})` : field.label;
}

export function formatMetricValue(value, field = {}) {
    if (value === null || value === undefined || value === "") {
        return EMPTY_VALUE;
    }

    if (typeof value === "number" && Number.isFinite(value)) {
        const precision = Number.isInteger(field.precision) ? field.precision : 4;
        const formatted = value.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: precision,
        });
        return formatted;
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    return String(value);
}

export function getQualityStatusColor(status) {
    if (status === "VERIFIED") {
        return "success";
    }
    if (status === "CORRECTED") {
        return "warning";
    }
    return "default";
}

export function buildSourceOptions(sources = []) {
    return sources.map((source) => ({
        value: source.id,
        label: `${source.source_code} · ${source.source_name}`,
    }));
}
